import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  AttendanceCopilotApiService,
  AttendanceCopilotSocketService,
  AttendanceCopilotStateService,
  CopilotAction,
  CopilotChatMessage,
  CopilotComposeBridgeService,
  CopilotState,
  DISPLAYABLE_ACTIONS,
} from 'lib-trend-core';
import { fromEvent, Observable, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { CopilotDrawerPane } from './attendance-copilot-drawer/attendance-copilot-drawer.component';

const SCROLL_ANCHOR_THRESHOLD_PX = 80;

@Component({
  selector: 'attendance-copilot-tab',
  templateUrl: './attendance-copilot-tab.component.html',
  styleUrls: ['./attendance-copilot-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AttendanceCopilotTabComponent
  implements OnInit, OnChanges, OnDestroy, AfterViewInit {

  @Input() idAttendance: string;
  // Gate: when false, the composer (with its quick questions) is hidden and a "locked" hint is
  // shown until the attendance reaches copilotMinMessages real conversation messages.
  @Input() copilotReady = false;
  @Input() copilotMinMessages = 20;
  // Closed attendances are read-only: the chat thread stays visible but the whole
  // footer (composer + unlock hint) is hidden.
  @Input() attendanceClosed = false;

  @ViewChild('thread') threadEl?: ElementRef<HTMLElement>;
  @ViewChild('quickToggle') quickToggleEl?: ElementRef<HTMLButtonElement>;
  @ViewChild('composer') composerEl?: ElementRef<HTMLTextAreaElement>;

  /** The pane of the drawer over the chat, or null while the chat shows (prototype D). */
  drawer: CopilotDrawerPane | null = null;

  /** Whether the quick questions show above the composer, opened from the button at its left. */
  quickQuestionsOpen = false;

  readonly state$: Observable<CopilotState> = this.stateService.select();

  readonly actions: { value: CopilotAction; label: string; icon: string }[] = [
    { value: 'suggestion', label: 'attendance.copilotTab.actionSuggestion', icon: 'ri-lightbulb-line' },
    { value: 'health',     label: 'attendance.copilotTab.actionHealth',     icon: 'ri-heart-pulse-line' },
    { value: 'playbook',   label: 'attendance.copilotTab.actionPlaybook',   icon: 'ri-map-2-line' },
    { value: 'sentiment',  label: 'attendance.copilotTab.actionSentiment',  icon: 'ri-emotion-line' },
  ];

  private readonly destroy$ = new Subject<void>();
  private prevSignature = '';
  private drawerOpener: HTMLElement | null = null;
  private outsideClick: Subscription | null = null;

  constructor(
    private readonly stateService: AttendanceCopilotStateService,
    private readonly apiService: AttendanceCopilotApiService,
    private readonly socketService: AttendanceCopilotSocketService,
    private readonly composeBridge: CopilotComposeBridgeService,
  ) { }

  // ─── lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.state$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
      const messages = this.displayableMessages(state);
      // Signature includes status so a pending bubble resolving to its (taller)
      // response also counts as a change — not just new messages being appended.
      const signature = messages
        .map((m) => `${m.requestId}:${m.side}:${m.status ?? ''}`)
        .join('|');

      if (signature !== this.prevSignature) {
        const initial = this.prevSignature === '';
        // Don't yank a user who scrolled up to read history; the "Nova mensagem"
        // pill covers that case. Always follow on the first paint, though.
        if (initial || state.scrollAnchored) {
          // requestAnimationFrame so the new (taller) DOM is laid out before we
          // measure scrollHeight. Smooth on incremental changes so the user
          // notices something arrived; instant on first paint / attendance switch.
          requestAnimationFrame(() => this.scrollToBottom(!initial));
        }
      }
      this.prevSignature = signature;
    });

    // The composer follows its draft after the render, so a draft cleared by a send or by an
    // attendance switch shrinks it back to one line.
    this.state$
      .pipe(map((state) => state.instructionDraft), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => requestAnimationFrame(() => this.fitComposer()));
  }

  ngOnChanges(changes: SimpleChanges): void {
    const id = changes['idAttendance']?.currentValue;
    if (id) {
      this.drawer = null;
      this.drawerOpener = null;
      this.closeQuickQuestions(false);
      this.stateService.resetForAttendance(id);
      this.socketService.joinAttendance(id);
      this.hydrate(id);
      return;
    }
    // Reload the chat when the attendance is closed (status change, same id) so the
    // thread reflects the persisted history instead of going blank/stale.
    const closedChange = changes['attendanceClosed'];
    if (closedChange && !closedChange.firstChange && this.idAttendance) {
      this.hydrate(this.idAttendance);
    }
  }

  ngAfterViewInit(): void {
    // The real scroll element is the SimpleBar wrapper that hosts this panel —
    // not the thread (which never overflows). Track the anchor state from it so
    // the "Nova mensagem" pill works and we don't yank users reading history.
    const scroller = this.getScroller();
    if (scroller) {
      fromEvent(scroller, 'scroll')
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.updateAnchoredFromScroller(scroller));
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.socketService.leaveCurrentChannel();
  }

  // ─── view helpers ──────────────────────────────────────────────────────────

  displayableMessages(state: CopilotState): CopilotChatMessage[] {
    return state.messages.filter((m) => DISPLAYABLE_ACTIONS.has(m.action));
  }

  hasMessages(state: CopilotState): boolean {
    return this.displayableMessages(state).length > 0;
  }

  isActionPending(state: CopilotState, action: CopilotAction): boolean {
    return state.messages.some(
      (m) => m.side === 'copilot' && m.action === action && m.status === 'pending',
    );
  }

  iconFor(action: string): string {
    const found = this.actions.find((a) => a.value === action);
    return found?.icon ?? 'ri-sparkling-line';
  }

  labelFor(action: string): string {
    const found = this.actions.find((a) => a.value === action);
    return found?.label ?? action;
  }

  /** Converts the sentiment averageScore (internal -1..+1 scale) to a 0-10 display score. */
  sentimentScore(msg: CopilotChatMessage): number {
    const raw = Number(msg?.result?.['averageScore'] ?? 0);
    if (!Number.isFinite(raw)) return 0;
    return Math.max(0, Math.min(10, Math.round(((raw + 1) / 2) * 10)));
  }

  isStreakHead(messages: CopilotChatMessage[], index: number): boolean {
    if (index === 0) return true;
    return messages[index - 1]?.side !== messages[index]?.side;
  }

  suggestionsOf(msg: CopilotChatMessage): any[] {
    const raw = msg?.result?.['suggestions'] as unknown;
    return Array.isArray(raw) ? (raw as any[]) : [];
  }

  /** Sends a reply suggestion to the main attendance chat composer. */
  addToChat(text: string): void {
    this.composeBridge.pushToComposer(text);
  }

  // ─── interactions ──────────────────────────────────────────────────────────

  /**
   * Opens the drawer from the summary, which is hidden while the drawer is open, and remembers the
   * control that opened it (RNF-09).
   */
  openDrawer(pane: CopilotDrawerPane): void {
    this.drawerOpener = document.activeElement as HTMLElement | null;
    this.drawer = pane;
  }

  /** Switches the drawer's pane; the focus will still return to the control that opened the drawer. */
  switchPane(pane: CopilotDrawerPane): void {
    this.drawer = pane;
  }

  /**
   * Closing returns to the chat where it was: at the bottom when it was following the conversation,
   * with the focus back on the control that opened the drawer once the summary shows again (RNF-09).
   * The focus does not scroll, so it never undoes the return to the bottom.
   */
  closeDrawer(): void {
    const opener = this.drawerOpener;
    this.drawer = null;
    this.drawerOpener = null;
    requestAnimationFrame(() => {
      if (this.stateService.snapshot.scrollAnchored) this.scrollToBottom(false);
      opener?.focus({ preventScroll: true });
    });
  }

  toggleQuickQuestions(event: Event): void {
    event.stopPropagation();
    if (this.quickQuestionsOpen) {
      this.closeQuickQuestions(false);
      return;
    }
    this.quickQuestionsOpen = true;
    // A click anywhere else closes the list (the whisper menu's pattern), listened to only while it is
    // open: a permanent document listener would run change detection on every click in the app.
    this.outsideClick = fromEvent(document, 'click')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.closeQuickQuestions(false));
  }

  /** Closes the quick questions; with `focusToggle`, the focus returns to the button that opens them. */
  closeQuickQuestions(focusToggle: boolean): void {
    if (!this.quickQuestionsOpen) return;
    this.quickQuestionsOpen = false;
    this.outsideClick?.unsubscribe();
    this.outsideClick = null;
    if (focusToggle) this.quickToggleEl?.nativeElement.focus();
  }

  /** A quick question is one of the manual actions: picking it sends that action and closes the list. */
  askQuickQuestion(action: CopilotAction): void {
    this.closeQuickQuestions(true);
    this.execute(action);
  }

  onInstructionChange(value: string): void {
    this.stateService.setInstruction(value);
  }

  execute(action: CopilotAction): void {
    const { idAttendance } = this.stateService.snapshot;
    if (!idAttendance) return;

    const pendingId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const now = new Date().toISOString();

    this.stateService.appendAttendantMessage({
      requestId: pendingId,
      side: 'attendant',
      action,
      createdAt: now,
    });
    this.stateService.appendPendingCopilotMessage({
      requestId: pendingId,
      side: 'copilot',
      action,
      status: 'pending',
      createdAt: now,
    });

    this.apiService.triggerAction(idAttendance, action)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => this.stateService.replaceRequestId(pendingId, res.requestId),
        error: () => this.stateService.rejectCopilotMessage(
          pendingId,
          'attendance.copilot.sendActionError',
        ),
      });
  }

  askCopilot(): void {
    const { idAttendance, instructionDraft } = this.stateService.snapshot;
    if (!idAttendance) return;

    const question = instructionDraft?.trim();
    if (!question) return;

    const pendingId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const now = new Date().toISOString();

    this.stateService.appendAttendantMessage({
      requestId: pendingId,
      side: 'attendant',
      action: 'ask',
      instruction: question,
      createdAt: now,
    });
    this.stateService.appendPendingCopilotMessage({
      requestId: pendingId,
      side: 'copilot',
      action: 'ask',
      status: 'pending',
      createdAt: now,
    });
    this.stateService.clearInstruction();

    this.apiService.triggerAction(idAttendance, 'ask', question)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => this.stateService.replaceRequestId(pendingId, res.requestId),
        error: () => this.stateService.rejectCopilotMessage(
          pendingId,
          'attendance.copilot.sendQuestionError',
        ),
      });
  }

  onComposerKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return;
    event.preventDefault();
    this.askCopilot();
  }

  /**
   * Fits the composer to its text: one line at rest, growing up to `maxRows`, then a scrollbar —
   * the main chat's `autoResizeTextArea` rule.
   */
  private fitComposer(maxRows = 3): void {
    const el = this.composerEl?.nativeElement;
    if (!el) return;
    const cs = getComputedStyle(el);
    const lineHeight = parseFloat(cs.lineHeight) || 20;
    const borders = parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
    const paddings = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
    const maxHeight = Math.round(maxRows * lineHeight + paddings + borders);
    el.style.height = 'auto';
    const needed = el.scrollHeight + borders;
    el.style.height = Math.min(needed, maxHeight) + 'px';
    el.style.overflowY = needed > maxHeight ? 'auto' : 'hidden';
  }

  retry(msg: CopilotChatMessage): void {
    if (msg.action === 'ask') {
      const question = (msg.instruction || '').trim();
      if (!question) return;
      this.stateService.setInstruction(question);
      this.askCopilot();
      return;
    }
    this.execute(msg.action as CopilotAction);
  }

  // ─── scroll ────────────────────────────────────────────────────────────────

  scrollToBottom(smooth: boolean): void {
    const scroller = this.getScroller();
    if (!scroller) return;
    scroller.scrollTo({ top: scroller.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
    // Optimistically anchor so a response arriving right after still follows;
    // the scroll listener corrects this if the user scrolls away.
    if (!this.stateService.snapshot.scrollAnchored) {
      this.stateService.setScrollAnchored(true);
    }
  }

  /**
   * Resolves the element that actually scrolls. This panel lives inside a
   * SimpleBar container, so its `.simplebar-content-wrapper` is the real
   * scroller — the thread's own `overflow-y` never activates because it has no
   * bounded height. Falls back to the nearest scrollable ancestor / the thread.
   */
  private getScroller(): HTMLElement | null {
    const thread = this.threadEl?.nativeElement;
    if (!thread) return null;

    const simplebar = thread.closest('.simplebar-content-wrapper') as HTMLElement | null;
    if (simplebar) return simplebar;

    let el: HTMLElement | null = thread;
    while (el) {
      const overflowY = getComputedStyle(el).overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight - el.clientHeight > 1) {
        return el;
      }
      el = el.parentElement;
    }
    return thread;
  }

  private updateAnchoredFromScroller(scroller: HTMLElement): void {
    const distanceFromBottom = scroller.scrollHeight - (scroller.scrollTop + scroller.clientHeight);
    const anchored = distanceFromBottom <= SCROLL_ANCHOR_THRESHOLD_PX;
    if (anchored !== this.stateService.snapshot.scrollAnchored) {
      this.stateService.setScrollAnchored(anchored);
    }
  }

  // ─── data ──────────────────────────────────────────────────────────────────

  private hydrate(idAttendance: string): void {
    this.stateService.setLoading(true);
    this.apiService.getChatHistory(idAttendance, { limit: 50 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          // Ignore a stale response: if the attendance changed while this request
          // was in flight, applying it would show another attendance's chat.
          if (this.idAttendance !== idAttendance) return;
          this.stateService.loadHistory(res?.data ?? []);
          requestAnimationFrame(() => this.scrollToBottom(false));
        },
        error: () => {
          if (this.idAttendance !== idAttendance) return;
          this.stateService.loadHistory([]);
        },
      });
  }
}
